import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { OrganizationStateService } from 'src/app/shared/organization-state.service';
import { AppProfile } from '../../../../shared/app-profile/app-profile';

@Injectable({ providedIn: 'root' })
export class AddUserService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly orgState: OrganizationStateService,
    private readonly appProfile: AppProfile
  ) {}

  getPermissionSets(orgId): Observable<any[]> {
    const token = this.authService.token;
    const headers = this.authService.getCommonHeaders(token, '2.0.0'); // Use the correct API version
    const url = orgId ? `${this.appProfile.epUs}/auth-ms/companies/${orgId}/permission-sets`: `${this.appProfile.epUs}/auth-ms/permission-sets`;
    return this.http.get<any[]>(url, { headers });
  }

  addUser(user: any): Observable<any> {
    const token = this.authService.token;
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.post<any>(`${this.appProfile.epUs}/user-management-ms/users`, user, { headers });
  }

  /**
   * Returns an observable with orgId and permissionSets, using permission-aware logic.
   */
  getOrgIdAndPermissionSets() {
    if (this.authService.hasPermission && this.authService.hasPermission('AccessProductSupportConsole')) {
      return this.orgState.orgResponse$.pipe(
        switchMap(org => {
          const orgId = org?.orgId || org?.id || null;
          if (!orgId) return of({ orgId: null, permissionSets: [] });
          return this.getPermissionSets(orgId).pipe(
            map(sets => ({ orgId, permissionSets: this.normalizePermissionSets(sets) }))
          );
        })
      );
    } else {
      const orgId = this.authService.userTopOrgId;
      return this.getPermissionSets(orgId).pipe(
        map(sets => ({ orgId, permissionSets: this.normalizePermissionSets(sets) }))
      );
    }
  }

  private normalizePermissionSets(sets: any): any[] {
    if (Array.isArray(sets)) {
      return sets;
    } else if (sets && typeof sets === 'object') {
      for (const key of Object.keys(sets)) {
        if (Array.isArray(sets[key])) {
          return sets[key];
        }
      }
      return [];
    } else {
      return [];
    }
  }

  randomString(length: number): string {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  randomNpiNumber(): string {
    let npi = '';
    for (let i = 0; i < 10; i++) {
      npi += Math.floor(Math.random() * 10).toString();
    }
    return npi;
  }

  generateUsername(firstName: string, lastName: string): Observable<any> {
    const token = this.authService.token;
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    const url = `${this.appProfile.epUs}/user-management-ms/generated-username?firstName=${encodeURIComponent(firstName)}&lastName=${encodeURIComponent(lastName)}`;
    
    console.log(`Calling username generation API: ${url}`);
    
    return this.http.get<any>(url, { headers });
  }

  generatePassword(): Observable<any> {
    const token = this.authService.token;
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    const url = `${this.appProfile.epUs}/user-management-ms/generated-password`;
    
    console.log(`Calling password generation API: ${url}`);
    
    return this.http.get<any>(url, { headers });
  }
}
